// Състоянието на филтрите в колоните на една справка: изборът по колони,
// филтрираните редове, описанията за попъпа на всяка колона (опциите с
// фасетни броячи се смятат чак когато попъпът се отвори) и редът „Филтри: …"
// над таблицата. Логиката е в `columnFilterModel.ts`; тук е само React
// обвивката. `specs` трябва да е мемоизиран (useMemo по `t`) — иначе
// филтрирането се прави наново на всеки рендер.
import { useCallback, useMemo, useState } from "react";
import {
  applyColumnFilters,
  clearFilterState,
  describeFilter,
  emptyValueOf,
  facetOptions,
  facetTotal,
  isFilterActive,
  rangeBounds,
  SEARCH_THRESHOLD,
  type FacetOption,
  type FilterDescription,
  type FilterSpec,
  type FilterSpecs,
  type FilterState,
  type FilterValue,
  type NumberRange,
} from "./columnFilterModel";

// Секция в попъпа на колона. Колона, която показва няколко неща в една
// клетка (напр. „Документ" = тип + номер + склад на средна ширина), носи
// няколко секции.
export type ListFilterSection = {
  kind: "list";
  key: string;
  title: string;
  selected: string[];
  getOptions: () => FacetOption[];
  searchable?: boolean;
  onChange: (values: string[]) => void;
};

export type RangeFilterSection = {
  kind: "range";
  key: string;
  title: string;
  value: NumberRange;
  getBounds: () => [number, number] | null;
  format: (value: number) => string;
  onChange: (range: NumberRange) => void;
};

export type ColumnFilterSection = ListFilterSection | RangeFilterSection;

// Секциите на справка по ключ; липсващ ключ = колоната не се филтрира (напр.
// „Плащане" при заварени данни без плащане)
export type ColumnSections<K extends string> = Partial<Record<K, ColumnFilterSection>>;

// Секциите за една колона (без липсващите); празно → колоната е без фуния
export const sectionList = (...sections: (ColumnFilterSection | undefined)[]) => {
  const list = sections.filter((section): section is ColumnFilterSection => !!section);
  return list.length ? list : undefined;
};

// Всички секции на справката — бутонът „Филтри" на телефон
export const allSections = <K extends string>(sections: ColumnSections<K>) =>
  sectionList(...Object.values<ColumnFilterSection | undefined>(sections)) ?? [];

// Елемент от реда „Филтри: …" над таблицата
export type FilterSummaryItem = FilterDescription & { key: string; onRemove: () => void };

// Броят на избраното в секциите — значката на колоната / бутона „Филтри"
export const selectedCountOf = (sections: ColumnFilterSection[]) =>
  sections.reduce(
    (sum, section) =>
      sum +
      (section.kind === "list"
        ? section.selected.length
        : isFilterActive(section.value)
          ? 1
          : 0),
    0,
  );

// Търсачка на секцията: изрично зададена или при дълъг списък
export const isSearchable = (section: ListFilterSection, optionsCount: number) =>
  section.searchable ?? optionsCount > SEARCH_THRESHOLD;

type Args<T, S extends FilterState> = {
  specs: FilterSpecs<T, S>;
  empty: S;
  // Изборът при отваряне, ако не е празният (напр. „Разнесени" в „Разнесени
  // отчети"). „Изчисти" винаги води до празното — не до този.
  initial?: S;
  // Всичко от периода/снимката — опциите и подсказките на диапазона
  universe: T[];
  // Периодът след търсенето (и сегмента) — филтрира се и се брои от него
  base: T[];
};

export const useColumnFilters = <T, S extends FilterState>({ specs, empty, initial, universe, base }: Args<T, S>) => {
  const [state, setState] = useState<S>(initial ?? empty);
  const rows = useMemo(() => applyColumnFilters(base, specs, state), [base, specs, state]);

  const setValue = useCallback(
    <K extends keyof S>(key: K, value: S[K]) => setState((current) => ({ ...current, [key]: value })),
    [],
  );
  const clearAll = useCallback(() => setState((current) => clearFilterState(current)), []);

  const facet = (key: keyof S & string) => facetOptions({ universe, base, specs, state, key });

  const section = (key: keyof S & string): ColumnFilterSection => {
    const spec = specs[key] as FilterSpec<T>;
    const value = state[key];
    if (spec.kind === "range") {
      return {
        kind: "range",
        key,
        title: spec.title,
        value: value as NumberRange,
        getBounds: () => rangeBounds(universe, spec),
        format: spec.format ?? ((number) => String(number)),
        onChange: (range) => setValue(key, range as S[typeof key]),
      };
    }
    return {
      kind: "list",
      key,
      title: spec.title,
      selected: value as string[],
      getOptions: () => facet(key),
      searchable: spec.searchable,
      onChange: (values) => setValue(key, values as S[typeof key]),
    };
  };

  const summary: FilterSummaryItem[] = (Object.keys(specs) as (keyof S & string)[]).flatMap((key) => {
    const description = describeFilter(specs[key] as FilterSpec<T>, state[key]);
    if (!description) return [];
    return [
      {
        ...description,
        key,
        onRemove: () => setValue(key, emptyValueOf(state[key] as FilterValue) as S[typeof key]),
      },
    ];
  });

  return {
    state,
    setValue,
    clearAll,
    rows,
    section,
    // Броячите на сегмент, вързан за колона (Всички / Изписване / Бракуване)
    facet,
    facetTotal: (key: keyof S & string) => facetTotal(base, specs, state, key),
    summary,
  };
};
